"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import type { FocusEvent, FormEvent, KeyboardEvent } from "react";
import type { Route } from "next";
import { useRouter } from "next/navigation";
import { Search } from "lucide-react";
import { event as gaEvent } from "@/lib/gtag";
import { trackJourneyEvent } from "@/lib/journey-analytics";
import { buildBlogSearchQueryState } from "@/lib/blog/search-href";
import {
  applySearchHrefParams,
  type SearchHrefParams,
} from "@/lib/blog/search-href-decoration";
import {
  clearBlogSearchNavigationState,
  normalizeBlogSearchNavigationHref,
  setPendingBlogSearchResultOrigin,
  type SearchNavigationSource,
} from "@/lib/blog/search-navigation";
import { fetchLiveBlogSuggestions } from "@/app/components/live-blog-suggestions";
import HomeGuidedSearchPanel from "@/app/components/HomeGuidedSearchPanel";
import HomeSearchResults from "@/app/components/HomeSearchResults";
import {
  buildSuggestionStatusMessage,
  MIN_LIVE_QUERY_LENGTH,
  useSearchTypeahead,
} from "@/app/components/search-typeahead";
import { SearchSuggestionIcon } from "@/app/components/search-suggestion-icon";
import {
  composeGuidedSearchQuery,
  LEVEL_OPTIONS,
  NEED_OPTIONS,
  SUBJECT_OPTIONS,
  type GuidedChipGroup,
  type LevelId,
  type NeedId,
  type SubjectId,
} from "@/app/components/guided-search-options";

type HomeSearchFormProps = {
  className?: string;
  placeholder?: string;
  searchHrefParams?: SearchHrefParams;
  disableLiveSuggestions?: boolean;
  suggestions?: readonly string[];
  analyticsPlacement?: "home_header" | "blog_hero";
  analyticsTool?: "home_search" | "blog_search";
  visualStyle?: "default" | "editorial" | "home";
  onGuidedNeedSelect?: (need: NeedId) => boolean;
  enableSuggestionList?: boolean;
};

const HOME_SEARCH_NAVIGATION_LOCK_TIMEOUT_MS = 10_000;
const MOBILE_GUIDED_SEARCH_QUERY = "(max-width: 767px)";
const SEARCH_UI_VARIANT = "progressive_v1";
const NEEDS_WITH_LEVEL_AND_SUBJECT = new Set<NeedId>([
  "notes",
  "tuition",
  "practicals",
]);
type HomeSearchAnalyticsSource = "submit" | "guided_submit";
type HomeSearchInputMode = "free_text" | "guided_chips" | "suggestion";
type DesktopCloseReason = "backdrop" | "button" | "escape" | "outside";

function isMobileGuidedSearchViewport(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia === "function") {
    return window.matchMedia(MOBILE_GUIDED_SEARCH_QUERY).matches;
  }
  return window.innerWidth < 768;
}

function createSearchAttemptId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `search-${crypto.randomUUID()}`;
  }
  return `search-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getFirstActionTimeBucket(elapsedMs: number): string {
  if (elapsedMs < 2_000) return "under_2s";
  if (elapsedMs < 5_000) return "2_to_5s";
  if (elapsedMs < 15_000) return "5_to_15s";
  return "15s_plus";
}

function optionLabel<T extends string>(
  options: ReadonlyArray<{ id: T; label: string }>,
  id: T | null
) {
  return options.find((option) => option.id === id)?.label;
}

export default function HomeSearchForm({
  className,
  placeholder = "Search a topic, subject, or programme",
  searchHrefParams,
  disableLiveSuggestions = false,
  suggestions = [],
  analyticsPlacement = "home_header",
  analyticsTool = "home_search",
  visualStyle = "default",
  onGuidedNeedSelect,
  enableSuggestionList = false,
}: HomeSearchFormProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const desktopSearchRootRef = useRef<HTMLDivElement | null>(null);
  const suggestionItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const blurTimeoutRef = useRef<number | null>(null);
  const navigationLockTimeoutRef = useRef<number | null>(null);
  const navigationLockHrefRef = useRef<string | null>(null);
  const suppressNextInputFocusOpenRef = useRef(false);
  const hasTrackedInputStartRef = useRef(false);
  const searchAttemptIdRef = useRef<string | null>(null);
  const searchAttemptOpenedAtRef = useRef<number | null>(null);
  const searchAttemptHasFirstActionRef = useRef(false);
  const searchAttemptOutcomeRef = useRef<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [isNavigationLocked, setIsNavigationLocked] = useState(false);
  const [isGuidedSearchOpen, setIsGuidedSearchOpen] = useState(false);
  const [isDesktopSearchModeOpen, setIsDesktopSearchModeOpen] = useState(false);
  const [hasDesktopSearchModeEntered, setHasDesktopSearchModeEntered] =
    useState(false);
  const [desktopFreeText, setDesktopFreeText] = useState("");
  const [selectedDesktopNeed, setSelectedDesktopNeed] = useState<NeedId | null>(
    null
  );
  const [selectedDesktopLevel, setSelectedDesktopLevel] =
    useState<LevelId | null>(null);
  const [selectedDesktopSubject, setSelectedDesktopSubject] =
    useState<SubjectId | null>(null);

  const startSearchAttempt = useCallback(
    (source: "desktop_focus" | "mobile_focus") => {
      let createdAttempt = false;
      if (
        searchAttemptIdRef.current === null ||
        searchAttemptOutcomeRef.current !== null
      ) {
        createdAttempt = true;
        searchAttemptIdRef.current = createSearchAttemptId();
        searchAttemptOpenedAtRef.current = Date.now();
        searchAttemptHasFirstActionRef.current = false;
        searchAttemptOutcomeRef.current = null;
        hasTrackedInputStartRef.current = false;
      }
      if (!createdAttempt) return;
      trackJourneyEvent("search_attempt", {
        action: "open",
        journey_name: "home_search_attempt",
        search_attempt_id: searchAttemptIdRef.current,
        search_ui_variant: SEARCH_UI_VARIANT,
        source_surface: source,
        tool: analyticsTool,
      });
    },
    [analyticsTool]
  );

  const recordFirstSearchAction = useCallback(
    (action: "input_started" | "guided_chip_select") => {
      if (searchAttemptHasFirstActionRef.current) return;
      searchAttemptHasFirstActionRef.current = true;
      const elapsedMs = Math.max(
        0,
        Date.now() - (searchAttemptOpenedAtRef.current ?? Date.now())
      );
      trackJourneyEvent("search_attempt", {
        action: "first_action",
        journey_name: "home_search_attempt",
        search_attempt_id: searchAttemptIdRef.current ?? undefined,
        search_ui_variant: SEARCH_UI_VARIANT,
        source_surface: action,
        time_to_first_action_bucket: getFirstActionTimeBucket(elapsedMs),
        tool: analyticsTool,
      });
    },
    [analyticsTool]
  );

  const recordSearchAttemptStage = useCallback(
    (
      action: "submitted" | "suggestion_selected" | "view_all_selected",
      searchInputMode: HomeSearchInputMode
    ) => {
      trackJourneyEvent("search_attempt", {
        action,
        journey_name: "home_search_attempt",
        search_attempt_id: searchAttemptIdRef.current ?? undefined,
        search_input_mode: searchInputMode,
        search_ui_variant: SEARCH_UI_VARIANT,
        tool: analyticsTool,
      });
    },
    [analyticsTool]
  );

  const recordSearchAttemptOutcome = useCallback(
    (
      outcome: "abandoned_before_submit" | "result_selected" | "zero_results",
      closeReason?: DesktopCloseReason | "navigation",
      selection?: { resultPosition: number; targetId: string }
    ) => {
      if (searchAttemptOutcomeRef.current !== null) return;
      searchAttemptOutcomeRef.current = outcome;
      trackJourneyEvent("search_attempt", {
        action: "outcome",
        close_reason: closeReason,
        journey_name: "home_search_attempt",
        search_attempt_id: searchAttemptIdRef.current ?? undefined,
        search_attempt_outcome: outcome,
        search_ui_variant: SEARCH_UI_VARIANT,
        result_position: selection?.resultPosition,
        target_id: selection?.targetId,
        tool: analyticsTool,
      });
    },
    [analyticsTool]
  );

  const clearNavigationLock = useCallback(() => {
    if (navigationLockTimeoutRef.current !== null) {
      window.clearTimeout(navigationLockTimeoutRef.current);
      navigationLockTimeoutRef.current = null;
    }
    navigationLockHrefRef.current = null;
    setIsNavigationLocked(false);
  }, []);

  useEffect(
    () => () => {
      if (blurTimeoutRef.current !== null) {
        window.clearTimeout(blurTimeoutRef.current);
      }
      if (navigationLockTimeoutRef.current !== null) {
        window.clearTimeout(navigationLockTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!isDesktopSearchModeOpen) {
      setHasDesktopSearchModeEntered(false);
      return;
    }
    const animationFrame = window.requestAnimationFrame(() => {
      setHasDesktopSearchModeEntered(true);
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [isDesktopSearchModeOpen]);

  const tryLockNavigation = useCallback(
    (href: string): boolean => {
      const normalizedHref =
        normalizeBlogSearchNavigationHref(href) ?? href.trim();
      if (!normalizedHref || navigationLockHrefRef.current !== null) {
        return false;
      }
      navigationLockHrefRef.current = normalizedHref;
      setIsNavigationLocked(true);
      if (navigationLockTimeoutRef.current !== null) {
        window.clearTimeout(navigationLockTimeoutRef.current);
      }
      navigationLockTimeoutRef.current = window.setTimeout(() => {
        clearNavigationLock();
      }, HOME_SEARCH_NAVIGATION_LOCK_TIMEOUT_MS);
      return true;
    },
    [clearNavigationLock]
  );

  const fetchSuggestions = useCallback(
    ({
      query,
      limit,
      signal,
    }: {
      query: string;
      limit: number;
      signal: AbortSignal;
    }) =>
      fetchLiveBlogSuggestions({
        query,
        limit,
        signal,
      }),
    []
  );
  const effectiveMinQueryLength = disableLiveSuggestions
    ? Number.MAX_SAFE_INTEGER
    : !enableSuggestionList
    ? Number.MAX_SAFE_INTEGER
    : MIN_LIVE_QUERY_LENGTH;

  const {
    preparedSuggestions,
    filteredSuggestions,
    normalizedInputValue,
    liveSuggestionStatus,
    usesLiveSuggestions,
    suggestionBadgeLabel,
  } = useSearchTypeahead({
    inputValue,
    suggestions,
    isComposing,
    fetchSuggestions,
    minQueryLength: effectiveMinQueryLength,
  });

  const hasVisibleSuggestions =
    enableSuggestionList && isSuggestionOpen && filteredSuggestions.length > 0;
  const hasManualDesktopQuery =
    desktopFreeText.trim().length >= MIN_LIVE_QUERY_LENGTH;
  const showDesktopGuidedChoices =
    isDesktopSearchModeOpen && !hasManualDesktopQuery;
  const showDesktopGuidedDetails =
    selectedDesktopNeed !== null &&
    NEEDS_WITH_LEVEL_AND_SUBJECT.has(selectedDesktopNeed);
  const showDesktopNeedStep = selectedDesktopNeed === null;
  const showDesktopLevelStep =
    showDesktopGuidedDetails && selectedDesktopLevel === null;
  const showDesktopSubjectStep =
    showDesktopGuidedDetails &&
    selectedDesktopLevel !== null &&
    selectedDesktopSubject === null;
  const isDesktopGuidedComplete =
    selectedDesktopNeed !== null &&
    (!showDesktopGuidedDetails ||
      (selectedDesktopLevel !== null && selectedDesktopSubject !== null));
  const selectedDesktopTrail = [
    optionLabel(NEED_OPTIONS, selectedDesktopNeed),
    optionLabel(LEVEL_OPTIONS, selectedDesktopLevel),
    optionLabel(SUBJECT_OPTIONS, selectedDesktopSubject),
  ].filter(Boolean);
  const suggestionListId = "home-search-suggestion-list";
  const activeSuggestionId =
    hasVisibleSuggestions && activeSuggestionIndex >= 0
      ? `home-search-suggestion-${activeSuggestionIndex}`
      : undefined;
  const suggestionStatusMessage = useMemo(
    () =>
      buildSuggestionStatusMessage({
        liveSuggestionStatus,
        normalizedInputValueLength: normalizedInputValue.length,
        minQueryLength: effectiveMinQueryLength,
        hasVisibleSuggestions,
        activeSuggestionIndex,
        filteredSuggestions,
      }),
    [
      activeSuggestionIndex,
      filteredSuggestions,
      hasVisibleSuggestions,
      liveSuggestionStatus,
      effectiveMinQueryLength,
      normalizedInputValue.length,
    ]
  );

  useEffect(() => {
    if (!isSuggestionOpen || filteredSuggestions.length === 0) {
      setActiveSuggestionIndex(-1);
      return;
    }
    setActiveSuggestionIndex((previous) =>
      previous >= filteredSuggestions.length
        ? filteredSuggestions.length - 1
        : previous
    );
  }, [filteredSuggestions, isSuggestionOpen]);

  useEffect(() => {
    if (!hasVisibleSuggestions || activeSuggestionIndex < 0) return;
    suggestionItemRefs.current[activeSuggestionIndex]?.scrollIntoView({
      block: "nearest",
    });
  }, [activeSuggestionIndex, hasVisibleSuggestions]);

  const closeSuggestionMenu = useCallback(() => {
    if (blurTimeoutRef.current !== null) {
      window.clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsSuggestionOpen(false);
    setActiveSuggestionIndex(-1);
  }, []);

  const openDesktopSearchMode = () => {
    startSearchAttempt("desktop_focus");
    setIsDesktopSearchModeOpen((wasOpen) => {
      if (!wasOpen) {
        gaEvent("tool_action", {
          tool: analyticsTool,
          action: "guided_open",
          placement: analyticsPlacement,
          search_ui_variant: SEARCH_UI_VARIANT,
          source: "desktop_focus",
        });
      }
      return true;
    });
  };

  const pushSearchHref = (
    query: string,
    source: "submit" | "suggestion",
    searchInputMode: HomeSearchInputMode
  ): boolean => {
    const targetSearchState = buildBlogSearchQueryState({ q: query });
    if (targetSearchState.queryLength === 0) {
      const targetHref = applySearchHrefParams("/blog", searchHrefParams);
      if (!tryLockNavigation(targetHref)) {
        return false;
      }
      clearBlogSearchNavigationState();
      startTransition(() => {
        router.push<"/blog">(targetHref as Route<"/blog">);
      });
      return true;
    }
    const targetHref = applySearchHrefParams(
      targetSearchState.href,
      searchHrefParams
    );
    if (!tryLockNavigation(targetHref)) {
      return false;
    }
    const resultSource: SearchNavigationSource =
      analyticsTool === "blog_search"
        ? source
        : source === "submit"
        ? "home_submit"
        : "home_suggestion";
    setPendingBlogSearchResultOrigin({
      href: targetHref,
      searchAttemptId: searchAttemptIdRef.current ?? undefined,
      searchInputMode,
      source: resultSource,
    });
    startTransition(() => {
      router.push<"/blog">(targetHref as Route<"/blog">);
    });
    return true;
  };

  const selectSuggestion = (suggestion: string) => {
    startSearchAttempt(
      isMobileGuidedSearchViewport() ? "mobile_focus" : "desktop_focus"
    );
    const targetSearchState = buildBlogSearchQueryState({ q: suggestion });
    closeSuggestionMenu();
    setIsDesktopSearchModeOpen(false);
    const didNavigate = pushSearchHref(
      targetSearchState.rawQuery,
      "suggestion",
      "suggestion"
    );
    if (!didNavigate) {
      return;
    }
    gaEvent("tool_action", {
      tool: analyticsTool,
      action: "select_suggestion",
      placement: analyticsPlacement,
      source: usesLiveSuggestions ? "typeahead_live" : "typeahead_static",
      search_input_mode: "suggestion",
      query_length: targetSearchState.queryLength,
      search_ui_variant: SEARCH_UI_VARIANT,
    });
    recordSearchAttemptStage("suggestion_selected", "suggestion");
    recordSearchAttemptStage("submitted", "suggestion");
  };

  const submitHomeSearch = (
    query: string,
    analyticsSource: HomeSearchAnalyticsSource = "submit",
    searchInputMode: HomeSearchInputMode = "free_text",
    guidedChipCount = 0
  ) => {
    startSearchAttempt(
      isMobileGuidedSearchViewport() ? "mobile_focus" : "desktop_focus"
    );
    const targetSearchState = buildBlogSearchQueryState({ q: query });
    const effectiveQueryLength = targetSearchState.queryLength;
    closeSuggestionMenu();
    setIsDesktopSearchModeOpen(false);

    const didNavigate = pushSearchHref(
      targetSearchState.rawQuery,
      "submit",
      searchInputMode
    );
    if (!didNavigate) {
      return;
    }

    if (effectiveQueryLength === 0) {
      gaEvent("tool_search_empty_submit", {
        tool: analyticsTool,
        placement: analyticsPlacement,
        source: analyticsSource,
        search_input_mode: searchInputMode,
        guided_chip_count: guidedChipCount,
        search_ui_variant: SEARCH_UI_VARIANT,
      });
    }

    gaEvent("tool_search", {
      tool: analyticsTool,
      placement: analyticsPlacement,
      query_length: effectiveQueryLength,
      source: analyticsSource,
      search_input_mode: searchInputMode,
      guided_chip_count: guidedChipCount,
      search_ui_variant: SEARCH_UI_VARIANT,
    });
    if (effectiveQueryLength === 0) {
      recordSearchAttemptOutcome("abandoned_before_submit", "navigation");
    } else {
      recordSearchAttemptStage("submitted", searchInputMode);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      (event.nativeEvent as { isComposing?: boolean }).isComposing ||
      isComposing
    ) {
      return;
    }

    const guidedChipCount = [
      selectedDesktopNeed,
      selectedDesktopLevel,
      selectedDesktopSubject,
    ].filter(Boolean).length;
    submitHomeSearch(
      inputValue,
      guidedChipCount > 0 ? "guided_submit" : "submit",
      guidedChipCount > 0 ? "guided_chips" : "free_text",
      guidedChipCount
    );
  };

  const openGuidedSearch = () => {
    startSearchAttempt("mobile_focus");
    closeSuggestionMenu();
    inputRef.current?.blur();
    setIsGuidedSearchOpen(true);
    gaEvent("tool_action", {
      tool: analyticsTool,
      action: "guided_open",
      placement: analyticsPlacement,
      search_ui_variant: SEARCH_UI_VARIANT,
      source: "mobile_focus",
    });
  };

  const closeGuidedSearch = (reason: string) => {
    setIsGuidedSearchOpen(false);
    inputRef.current?.blur();
    gaEvent("tool_action", {
      tool: analyticsTool,
      action: "guided_close",
      placement: analyticsPlacement,
      source: "mobile_guided",
      search_ui_variant: SEARCH_UI_VARIANT,
      value: reason,
    });
    recordSearchAttemptOutcome(
      "abandoned_before_submit",
      reason === "backdrop" || reason === "button" || reason === "escape"
        ? reason
        : "outside"
    );
  };

  const closeDesktopSearchMode = useCallback(
    ({
      restoreFocus,
      reason,
    }: {
      restoreFocus: boolean;
      reason: DesktopCloseReason;
    }) => {
      closeSuggestionMenu();
      setIsDesktopSearchModeOpen(false);
      setSelectedDesktopNeed(null);
      setSelectedDesktopLevel(null);
      setSelectedDesktopSubject(null);
      if (restoreFocus) {
        if (document.activeElement === inputRef.current) {
          suppressNextInputFocusOpenRef.current = false;
        } else {
          suppressNextInputFocusOpenRef.current = true;
          window.setTimeout(() => inputRef.current?.focus(), 0);
        }
      } else {
        inputRef.current?.blur();
      }
      gaEvent("tool_action", {
        tool: analyticsTool,
        action: "guided_close",
        placement: analyticsPlacement,
        source: "desktop_guided",
        search_ui_variant: SEARCH_UI_VARIANT,
        value: reason,
      });
      recordSearchAttemptOutcome("abandoned_before_submit", reason);
    },
    [
      analyticsPlacement,
      analyticsTool,
      closeSuggestionMenu,
      recordSearchAttemptOutcome,
    ]
  );

  useEffect(() => {
    if (!isDesktopSearchModeOpen) return;

    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (desktopSearchRootRef.current?.contains(target)) return;
      closeDesktopSearchMode({ restoreFocus: false, reason: "outside" });
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () =>
      document.removeEventListener(
        "pointerdown",
        handleOutsidePointerDown,
        true
      );
  }, [closeDesktopSearchMode, isDesktopSearchModeOpen]);

  const submitGuidedSearch = (
    query: string,
    metadata: { guidedChipCount: number }
  ) => {
    setInputValue(query);
    setIsGuidedSearchOpen(false);
    suppressNextInputFocusOpenRef.current = true;
    submitHomeSearch(
      query,
      metadata.guidedChipCount > 0 ? "guided_submit" : "submit",
      metadata.guidedChipCount > 0 ? "guided_chips" : "free_text",
      metadata.guidedChipCount
    );
  };

  const trackGuidedChipToggle = ({
    group,
    id,
    isSelected,
  }: {
    group: string;
    id: string;
    isSelected: boolean;
  }) => {
    if (isSelected) recordFirstSearchAction("guided_chip_select");
    gaEvent("tool_action", {
      tool: analyticsTool,
      action: isSelected ? "guided_chip_select" : "guided_chip_clear",
      placement: analyticsPlacement,
      source: "mobile_guided",
      search_ui_variant: SEARCH_UI_VARIANT,
      value: group,
      value2: id,
    });
  };

  const trackDesktopGuidedChipToggle = ({
    group,
    id,
    isSelected,
  }: {
    group: GuidedChipGroup;
    id: string;
    isSelected: boolean;
  }) => {
    if (isSelected) recordFirstSearchAction("guided_chip_select");
    gaEvent("tool_action", {
      tool: analyticsTool,
      action: isSelected ? "guided_chip_select" : "guided_chip_clear",
      placement: analyticsPlacement,
      source: "desktop_guided",
      search_ui_variant: SEARCH_UI_VARIANT,
      value: group,
      value2: id,
    });
  };

  const syncDesktopGuidedQuery = ({
    need,
    level,
    subject,
  }: {
    need: NeedId | null;
    level: LevelId | null;
    subject: SubjectId | null;
  }) => {
    setInputValue(
      composeGuidedSearchQuery({
        freeText: desktopFreeText,
        need,
        level,
        subject,
      })
    );
    setIsSuggestionOpen(false);
    setActiveSuggestionIndex(-1);
  };

  const handleDesktopChipToggle = ({
    group,
    id,
  }: {
    group: GuidedChipGroup;
    id: NeedId | LevelId | SubjectId;
  }) => {
    if (group === "need") {
      const nextNeed = selectedDesktopNeed === id ? null : (id as NeedId);
      trackDesktopGuidedChipToggle({
        group,
        id,
        isSelected: nextNeed !== null,
      });
      if (nextNeed && onGuidedNeedSelect?.(nextNeed)) {
        closeSuggestionMenu();
        setIsDesktopSearchModeOpen(false);
        setSelectedDesktopNeed(null);
        setSelectedDesktopLevel(null);
        setSelectedDesktopSubject(null);
        inputRef.current?.blur();
        return;
      }
      setSelectedDesktopNeed(nextNeed);
      setSelectedDesktopLevel(null);
      setSelectedDesktopSubject(null);
      syncDesktopGuidedQuery({
        need: nextNeed,
        level: null,
        subject: null,
      });
      return;
    }

    if (group === "level") {
      const nextLevel = selectedDesktopLevel === id ? null : (id as LevelId);
      trackDesktopGuidedChipToggle({
        group,
        id,
        isSelected: nextLevel !== null,
      });
      setSelectedDesktopLevel(nextLevel);
      setSelectedDesktopSubject(null);
      syncDesktopGuidedQuery({
        need: selectedDesktopNeed,
        level: nextLevel,
        subject: null,
      });
      return;
    }

    const nextSubject =
      selectedDesktopSubject === id ? null : (id as SubjectId);
    trackDesktopGuidedChipToggle({
      group,
      id,
      isSelected: nextSubject !== null,
    });
    setSelectedDesktopSubject(nextSubject);
    syncDesktopGuidedQuery({
      need: selectedDesktopNeed,
      level: selectedDesktopLevel,
      subject: nextSubject,
    });
  };

  const resetDesktopGuidedSearch = () => {
    setSelectedDesktopNeed(null);
    setSelectedDesktopLevel(null);
    setSelectedDesktopSubject(null);
    syncDesktopGuidedQuery({ need: null, level: null, subject: null });
  };

  const handleInputBlur = () => {
    const closeSuggestionMenuWithDelay = () => {
      if (blurTimeoutRef.current !== null) {
        window.clearTimeout(blurTimeoutRef.current);
      }
      blurTimeoutRef.current = window.setTimeout(() => {
        setIsSuggestionOpen(false);
        setActiveSuggestionIndex(-1);
      }, 120);
    };
    closeSuggestionMenuWithDelay();
  };

  const handleInputBlurWithTarget = (event: FocusEvent<HTMLInputElement>) => {
    const nextFocusedElement = event.relatedTarget;
    if (
      nextFocusedElement instanceof HTMLElement &&
      nextFocusedElement.closest(`#${suggestionListId}`)
    ) {
      return;
    }
    handleInputBlur();
  };

  const handleSuggestionOptionBlur = (event: FocusEvent<HTMLButtonElement>) => {
    const nextFocusedElement = event.relatedTarget;
    if (
      nextFocusedElement instanceof HTMLElement &&
      (nextFocusedElement.closest(`#${suggestionListId}`) ||
        nextFocusedElement === inputRef.current)
    ) {
      return;
    }
    if (blurTimeoutRef.current !== null) {
      window.clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = window.setTimeout(() => {
      setIsSuggestionOpen(false);
      setActiveSuggestionIndex(-1);
    }, 120);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || isComposing) return;
    if (!isSuggestionOpen || filteredSuggestions.length === 0) {
      if (event.key === "Escape" && isDesktopSearchModeOpen) {
        event.preventDefault();
        closeDesktopSearchMode({ restoreFocus: true, reason: "escape" });
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      const nextIndex =
        activeSuggestionIndex < filteredSuggestions.length - 1
          ? activeSuggestionIndex + 1
          : 0;
      setActiveSuggestionIndex(nextIndex);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      const nextIndex =
        activeSuggestionIndex > 0
          ? activeSuggestionIndex - 1
          : filteredSuggestions.length - 1;
      setActiveSuggestionIndex(nextIndex);
      return;
    }

    if (event.key === "Escape") {
      closeSuggestionMenu();
      if (isDesktopSearchModeOpen) {
        closeDesktopSearchMode({ restoreFocus: true, reason: "escape" });
      }
      return;
    }

    if (event.key === "Enter" && activeSuggestionIndex >= 0) {
      event.preventDefault();
      const activeSuggestion = filteredSuggestions[activeSuggestionIndex];
      if (activeSuggestion) selectSuggestion(activeSuggestion);
    }
  };

  return (
    <>
      {isDesktopSearchModeOpen && (
        <button
          type="button"
          aria-label="Close focused search"
          className={`fixed inset-0 z-[9990] hidden cursor-default bg-slate-950/30 transition-opacity duration-200 motion-reduce:transition-none md:block ${
            hasDesktopSearchModeEntered ? "opacity-100" : "opacity-0"
          }`}
          onClick={() =>
            closeDesktopSearchMode({ restoreFocus: false, reason: "backdrop" })
          }
        />
      )}
      <div
        ref={desktopSearchRootRef}
        className={`relative transition-all duration-200 motion-reduce:transition-none ${
          isDesktopSearchModeOpen ? "md:z-[10000]" : ""
        }`}
      >
        <form
          action="/blog"
          method="get"
          className={className}
          data-search-ui-variant={SEARCH_UI_VARIANT}
          onSubmit={handleSubmit}
        >
          <label htmlFor="home-search-q" className="sr-only">
            Search notes, tuition, practicals, scholarships, bursaries, and
            dates
          </label>
          <div
            data-visual-style={visualStyle}
            className={
              visualStyle === "editorial"
                ? `border border-slate-300 bg-white p-1.5 shadow-[0_16px_50px_rgba(15,23,42,0.10)] transition-[border-radius] sm:p-2 ${
                    isDesktopSearchModeOpen ? "rounded-3xl" : "rounded-full"
                  }`
                : visualStyle === "home"
                ? "rounded-2xl border border-blue-200 bg-blue-50/95 p-1.5 shadow-[0_12px_28px_rgba(30,64,175,0.12)] backdrop-blur-sm sm:p-2"
                : "rounded-2xl border border-blue-100/80 bg-white/80 p-1.5 shadow-[0_12px_28px_rgba(15,23,42,0.10)] backdrop-blur-sm sm:p-2"
            }
          >
            <div className="relative flex items-center">
              <input
                ref={inputRef}
                id="home-search-q"
                name="q"
                type="text"
                value={inputValue}
                role="combobox"
                aria-describedby="home-search-hint home-search-suggestion-status"
                aria-autocomplete="list"
                aria-expanded={hasVisibleSuggestions}
                aria-haspopup="listbox"
                aria-controls={
                  hasVisibleSuggestions ? suggestionListId : undefined
                }
                aria-activedescendant={activeSuggestionId}
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                placeholder={placeholder}
                className={
                  visualStyle === "editorial"
                    ? "h-14 w-full rounded-full border-0 bg-white pl-5 pr-14 text-base text-slate-900 placeholder:text-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:h-16 sm:pl-7 sm:pr-20 sm:text-lg"
                    : "h-11 w-full rounded-xl border border-blue-100/80 bg-white/88 pl-4 pr-12 text-sm text-slate-800 shadow-[0_8px_18px_rgba(15,23,42,0.08)] placeholder:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:h-12 sm:pl-5 sm:pr-14"
                }
                onChange={(event) => {
                  if (
                    !hasTrackedInputStartRef.current &&
                    event.currentTarget.value.trim().length > 0
                  ) {
                    hasTrackedInputStartRef.current = true;
                    gaEvent("tool_action", {
                      tool: analyticsTool,
                      action: "input_started",
                      placement: analyticsPlacement,
                      source: isMobileGuidedSearchViewport()
                        ? "mobile_guided"
                        : "desktop_input",
                      search_input_mode: "free_text",
                      search_ui_variant: SEARCH_UI_VARIANT,
                    });
                    recordFirstSearchAction("input_started");
                  }
                  setInputValue(event.currentTarget.value);
                  setDesktopFreeText(event.currentTarget.value);
                  setSelectedDesktopNeed(null);
                  setSelectedDesktopLevel(null);
                  setSelectedDesktopSubject(null);
                  setIsSuggestionOpen(true);
                  setActiveSuggestionIndex(-1);
                }}
                onCompositionStart={() => setIsComposing(true)}
                onCompositionEnd={() => setIsComposing(false)}
                onFocus={() => {
                  if (suppressNextInputFocusOpenRef.current) {
                    suppressNextInputFocusOpenRef.current = false;
                    return;
                  }
                  if (isMobileGuidedSearchViewport()) {
                    openGuidedSearch();
                    return;
                  }
                  setDesktopFreeText(inputValue);
                  openDesktopSearchMode();
                  if (
                    filteredSuggestions.length > 0 ||
                    preparedSuggestions.length > 0 ||
                    normalizedInputValue.length >= MIN_LIVE_QUERY_LENGTH
                  ) {
                    setIsSuggestionOpen(true);
                  }
                }}
                onBlur={handleInputBlurWithTarget}
                onKeyDown={handleInputKeyDown}
              />
              <p id="home-search-hint" className="sr-only">
                Use up and down arrow keys to review suggestions, then press
                Enter to select.
              </p>
              <p
                id="home-search-suggestion-status"
                className="sr-only"
                aria-live="polite"
                aria-atomic="true"
              >
                {suggestionStatusMessage}
              </p>
              <button
                type="submit"
                disabled={isPending || isNavigationLocked}
                className={`absolute right-1.5 top-1/2 inline-flex -translate-y-1/2 items-center justify-center rounded-full bg-blue-700 text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:right-2 ${
                  visualStyle === "editorial"
                    ? "h-10 w-10 sm:h-12 sm:w-12"
                    : "h-9 w-9 hover:scale-105 sm:h-11 sm:w-11"
                } ${showDesktopGuidedChoices ? "md:hidden" : ""}`}
                aria-label="Search resources"
                title="Search"
              >
                <Search
                  aria-hidden="true"
                  className="h-4 w-4 sm:h-[18px] sm:w-[18px]"
                />
              </button>
            </div>
            {showDesktopGuidedChoices ? (
              <div className="mt-3 hidden border-t border-slate-200 px-3 pb-3 pt-4 md:block">
                {selectedDesktopTrail.length > 0 ? (
                  <div className="mb-5 flex items-center justify-between gap-3 rounded-xl bg-blue-50 px-3 py-2.5">
                    <p className="min-w-0 truncate text-sm font-semibold text-blue-950">
                      {selectedDesktopTrail.join(" / ")}
                    </p>
                    <button
                      type="button"
                      onClick={resetDesktopGuidedSearch}
                      className="shrink-0 text-sm font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4"
                    >
                      Start over
                    </button>
                  </div>
                ) : null}

                {showDesktopNeedStep ? (
                  <section aria-labelledby="desktop-guided-search-need-title">
                    <h3
                      id="desktop-guided-search-need-title"
                      className="text-base font-semibold text-slate-950"
                    >
                      What do you need?
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {NEED_OPTIONS.map(({ id, label, Icon }) => (
                        <button
                          key={id}
                          type="button"
                          aria-pressed={selectedDesktopNeed === id}
                          onClick={() =>
                            handleDesktopChipToggle({ group: "need", id })
                          }
                          className={`inline-flex min-h-10 items-center rounded-full border px-3 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                            selectedDesktopNeed === id
                              ? "border-blue-700 bg-blue-700 text-white shadow-sm"
                              : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
                          }`}
                        >
                          <Icon aria-hidden="true" className="mr-2 h-4 w-4" />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </section>
                ) : null}

                {showDesktopLevelStep ? (
                  <section aria-labelledby="desktop-guided-search-level-title">
                    <h3
                      id="desktop-guided-search-level-title"
                      className="text-base font-semibold text-slate-950"
                    >
                      What level?
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {LEVEL_OPTIONS.map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          aria-pressed={selectedDesktopLevel === id}
                          onClick={() =>
                            handleDesktopChipToggle({ group: "level", id })
                          }
                          className={`inline-flex min-h-10 items-center rounded-full border px-3 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                            selectedDesktopLevel === id
                              ? "border-blue-700 bg-blue-700 text-white shadow-sm"
                              : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </section>
                ) : null}

                {showDesktopSubjectStep ? (
                  <section aria-labelledby="desktop-guided-search-subject-title">
                    <h3
                      id="desktop-guided-search-subject-title"
                      className="text-base font-semibold text-slate-950"
                    >
                      Which subject?
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {SUBJECT_OPTIONS.map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          aria-pressed={selectedDesktopSubject === id}
                          onClick={() =>
                            handleDesktopChipToggle({ group: "subject", id })
                          }
                          className={`inline-flex min-h-10 items-center rounded-full border px-3 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                            selectedDesktopSubject === id
                              ? "border-blue-700 bg-blue-700 text-white shadow-sm"
                              : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </section>
                ) : null}

                {isDesktopGuidedComplete ? (
                  <button
                    type="submit"
                    disabled={isPending || isNavigationLocked}
                    className="inline-flex min-h-11 w-full items-center justify-center rounded-full bg-blue-700 px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <Search aria-hidden="true" className="mr-2 h-4 w-4" />
                    Show results
                  </button>
                ) : null}
              </div>
            ) : null}
            {hasVisibleSuggestions && (
              <div
                id={suggestionListId}
                className="mt-2 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white/95 p-2 shadow-[0_20px_45px_rgba(15,23,42,0.16)] backdrop-blur-sm"
                role="listbox"
                aria-label="Search suggestions"
              >
                <ul className="flex flex-col gap-1">
                  {filteredSuggestions.map((suggestion, index) => {
                    const isActive = index === activeSuggestionIndex;
                    return (
                      <li key={`${suggestion}-${index}`}>
                        <button
                          ref={(node) => {
                            suggestionItemRefs.current[index] = node;
                          }}
                          type="button"
                          disabled={isPending || isNavigationLocked}
                          id={`home-search-suggestion-${index}`}
                          role="option"
                          aria-selected={isActive}
                          tabIndex={-1}
                          className={`flex w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition ${
                            isActive
                              ? "bg-blue-600 text-white"
                              : "text-slate-700 hover:bg-blue-50"
                          }`}
                          onFocus={() => setActiveSuggestionIndex(index)}
                          onBlur={handleSuggestionOptionBlur}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            if (blurTimeoutRef.current !== null) {
                              window.clearTimeout(blurTimeoutRef.current);
                              blurTimeoutRef.current = null;
                            }
                          }}
                          onClick={() => selectSuggestion(suggestion)}
                          onKeyDown={(event) => {
                            if (event.key === "ArrowDown") {
                              event.preventDefault();
                              const nextIndex =
                                index < filteredSuggestions.length - 1
                                  ? index + 1
                                  : 0;
                              setActiveSuggestionIndex(nextIndex);
                              inputRef.current?.focus();
                              return;
                            }
                            if (event.key === "ArrowUp") {
                              event.preventDefault();
                              const nextIndex =
                                index > 0
                                  ? index - 1
                                  : filteredSuggestions.length - 1;
                              setActiveSuggestionIndex(nextIndex);
                              inputRef.current?.focus();
                              return;
                            }
                            if (event.key === "Escape") {
                              event.preventDefault();
                              closeSuggestionMenu();
                              if (isDesktopSearchModeOpen) {
                                closeDesktopSearchMode({
                                  restoreFocus: true,
                                  reason: "escape",
                                });
                                return;
                              }
                              suppressNextInputFocusOpenRef.current = true;
                              inputRef.current?.focus();
                              return;
                            }
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              selectSuggestion(suggestion);
                            }
                          }}
                        >
                          <span className="flex min-w-0 items-center">
                            <SearchSuggestionIcon
                              suggestion={suggestion}
                              isActive={isActive}
                            />
                            <span className="line-clamp-1">{suggestion}</span>
                          </span>
                          <span
                            className={`ml-3 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                              isActive
                                ? "border-white/50 text-white/90"
                                : "border-slate-200 text-slate-500"
                            }`}
                          >
                            {suggestionBadgeLabel}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          {visualStyle === "default" ? (
            <div
              className="pointer-events-none mt-3 h-px w-full bg-gradient-to-r from-transparent via-slate-300/50 to-transparent"
              aria-hidden="true"
            />
          ) : null}
          <HomeSearchResults
            query={inputValue}
            analyticsPlacement={analyticsPlacement}
            analyticsTool={analyticsTool}
            enabled={isDesktopSearchModeOpen && hasManualDesktopQuery}
            searchAttemptId={searchAttemptIdRef.current ?? undefined}
            searchInputMode={
              [
                selectedDesktopNeed,
                selectedDesktopLevel,
                selectedDesktopSubject,
              ].some(Boolean)
                ? "guided_chips"
                : "free_text"
            }
            onAttemptOutcome={(outcome, selection) =>
              outcome === "view_all_selected"
                ? recordSearchAttemptStage(
                    "view_all_selected",
                    [
                      selectedDesktopNeed,
                      selectedDesktopLevel,
                      selectedDesktopSubject,
                    ].some(Boolean)
                      ? "guided_chips"
                      : "free_text"
                  )
                : recordSearchAttemptOutcome(outcome, "navigation", selection)
            }
          />
        </form>
      </div>
      <HomeGuidedSearchPanel
        isOpen={isGuidedSearchOpen}
        initialQuery={inputValue}
        isSubmitting={isPending || isNavigationLocked}
        onClose={closeGuidedSearch}
        onChipToggle={(payload) => {
          trackGuidedChipToggle(payload);
          if (
            payload.group === "need" &&
            payload.isSelected &&
            onGuidedNeedSelect?.(payload.id as NeedId)
          ) {
            recordSearchAttemptOutcome("result_selected", "navigation");
            setIsGuidedSearchOpen(false);
            inputRef.current?.blur();
          }
        }}
        onInputStart={() => {
          if (!hasTrackedInputStartRef.current) {
            hasTrackedInputStartRef.current = true;
            gaEvent("tool_action", {
              tool: analyticsTool,
              action: "input_started",
              placement: analyticsPlacement,
              source: "mobile_guided",
              search_input_mode: "free_text",
              search_ui_variant: SEARCH_UI_VARIANT,
            });
            recordFirstSearchAction("input_started");
          }
        }}
        onSubmit={submitGuidedSearch}
      />
    </>
  );
}
